






































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { Route } from "vue-router";
// components
import UserDetails from "@/components/users/UserDetails.vue";
import UserMediaLibraryACL from "@/components/users/UserMediaLibraryACL.vue";
import UnsavedFormModal from "@/components/common/modals/UnsavedFormModal.vue";
import UserErrorModal from "@/components/users/modals/UserErrorModal.vue";
import HeaderActionButtons from "@/components/layouts/HeaderActionButtons.vue";
import UserResourcesACL from "@/components/users/UserResourcesACL.vue";
import UserUserGroups from "@/components/users/UserUserGroups.vue";
// types
import { SelectOption, User, UserDetailsDto, UserDetailsModel } from "@/services/users/types";
import { FormComponent, FormComponentModel } from "@/services/autoSettings/types";
import { Acl, EffectivePermissionAcl, ResourceType } from "@/services/security/types";
// service methods
import {
    getUser,
    getAvailableRoles,
    createUser,
    updateUser
} from "@/services/users/usersService";
import { getSetting, setSetting } from "@/services/autoSettings/autoSettingsService";
import { getUserAcl, getUserInheritedAcl } from "@/services/security/accessControlService";
import { validateForm } from "@/services/validation/validationService";
import { bus } from "@/bus";
import i18n from "@/i18n";

const authenticationModule = namespace("authentication");
@Component<UserForm>({
    beforeRouteLeave(to, from, next) {
        this.onBeforeRouteLeave(to, next);
    },
    components: {
        UserDetails,
        UserErrorModal,
        HeaderActionButtons,
        UserMediaLibraryACL,
        UnsavedFormModal,
        UserResourcesACL,
        UserUserGroups
    }
})
export default class UserForm extends Vue {
    @Prop() private userId!: string;
    // local state
    user = {
        editorProfileId: "",
        firstName: "",
        lastName: "",
        email: "",
        jobTitle: "",
        sendPasswordChangeEmail: false,
        password: "",
        repeatPassword: "",
        roles: [] as string[],
        acl: [] as Acl[],
        userGroupIds: [] as string[]
    } as UserDetailsDto;

    // used for comparing if form have changes
    priorUserState = JSON.parse(JSON.stringify(this.user)) as UserDetailsDto;

    availableRoles = [] as string[];
    componentsData = [] as FormComponentModel[];
    formState = false;
    acl = [] as Acl[];
    inheritedAcls = [] as EffectivePermissionAcl[];
    @authenticationModule.Getter("getCurrentUser") currentUser!: User;

    // used for comparing if form have changes
    localUser = {
        editorProfileId: "",
        firstName: "",
        lastName: "",
        email: "",
        sendPasswordChangeEmail: false,
        password: "",
        repeatPassword: "",
        roles: [] as string[],
        acl: [] as Acl[],
        userGroupIds: [] as string[]
    } as UserDetailsDto;

    nextRoute = "";
    saveClicked = false;
    continue = false;
    fetchingActive = false;
    aclLoaded = false;
    usersLoaded = false;
    routeParams = {};
    localId = this.userId || "";

    // lifecycle hooks
    created() {
        this.fetchData();
        this.initComponents();
    }

    // Watcher
    firstTime = true as boolean;
    @Watch("$route", { immediate: true, deep: true })
    onUrlChange(newVal: any) {
        if (this.firstTime) {
            this.firstTime = false;
        } else {
            this.fetchData();
        }
    }

    get userDetails(): any {
        return {
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            email: this.user.email,
            sendPasswordChangeEmail: this.user.sendPasswordChangeEmail,
            password: this.user.password,
            repeatPassword: this.user.password,
            jobTitle: this.user.jobTitle,
            cultureCode: this.user.cultureCode,
            timeZone: this.user.timeZone,
            editorProfileId: this.user.editorProfileId,
            roles: this.user.roles || []
        } as UserDetailsModel;
    }

    get components(): FormComponentModel[] {
        return this.componentsData.sort((x, y) => x.order - y.order);
    }

    get editMode() {
        return !!this.localId;
    }

    get componentsAutoSettingKey() : string {
        let settingsKey = "userFormComponents";
        settingsKey += this.localId ? "-edit" : "-create";
        return settingsKey;
    }

    get preventSave() {
        if (!this.editMode) return false;
        return !this.usersLoaded || !this.aclLoaded;
    }

    // methods
    async initComponents() {
        let autoSettingComponents = getSetting(this.componentsAutoSettingKey) as FormComponent[];
        const defaultComponents = [
            {
                componentName: "UserDetails",
                sectionName: i18n.t("common.details"),
                order: 0,
                collapseState: true
            },
            {
                componentName: "UserResourcesACL",
                sectionName: i18n.t("acl.title"),
                order: 1,
                collapseState: true
            },
            {
                componentName: "UserMediaLibraryACL",
                sectionName: i18n.t("acl.access"),
                order: 2,
                collapseState: true
            },
            {
                componentName: "UserUserGroups",
                sectionName: i18n.t("userGroups.title"),
                order: 3,
                collapseState: true
            }] as FormComponent[];

        if (!autoSettingComponents) {
            autoSettingComponents = await this.initAutoSettings(defaultComponents);
        } else {
            // Check if saved auto setting components has same component and if not then set to default components.
            var hasSameComponents = this.lodash.isEqual(
                this.lodash.sortBy(autoSettingComponents.map(component => component.componentName)),
                this.lodash.sortBy(defaultComponents.map(component => component.componentName))
            );
            if (hasSameComponents) {
                await this.updateComponents(defaultComponents, autoSettingComponents);
            } else {
                autoSettingComponents = await this.initAutoSettings(defaultComponents);
            }
        }

        this.componentsData = autoSettingComponents.map((x: FormComponent) => ({
            componentName: x.componentName,
            order: x.order,
            active: false,
            activeDropTarget: false,
            collapseState: x.collapseState,
            sectionName: x.sectionName
        }));
    }

    async initAutoSettings(defaultComponents: FormComponent[]) {
        await setSetting(this.componentsAutoSettingKey,
            defaultComponents);
        return getSetting(this.componentsAutoSettingKey);
    }

    async updateComponents(defaultComponents: FormComponent[], components: FormComponent[]) {
        let updateSettings = false;

        const componentsToBeAdded = defaultComponents
            .filter(x => !components.map(x => x.componentName).includes(x.componentName));
        const componentsToBeRemoved = components
            .filter(x => !defaultComponents
                .map(x => x.componentName).includes(x.componentName));
        if (componentsToBeAdded.length > 0) {
            components = components.concat(componentsToBeAdded);
        }
        if (componentsToBeRemoved.length > 0) {
            componentsToBeRemoved.forEach(cr => {
                components = components.filter(x => x.componentName === cr.componentName);
            });
        }

        if (componentsToBeAdded.length > 0 || componentsToBeRemoved.length > 0) {
            components.sort((a, b) => a.order - b.order)
                .forEach((el, index) => {
                    el.order = index;
                });
            updateSettings = true;
        }

        components.forEach(c => {
            const dc = defaultComponents.find(x => x.componentName === c.componentName);
            if (dc && dc.sectionName !== c.sectionName) {
                c.sectionName = dc.sectionName;
                updateSettings = true;
            }
        });

        if (updateSettings) {
            await setSetting(this.componentsAutoSettingKey,
                components);
        }

        return components;
    }

    back() {
        window.history.go(-1);
    }

    onBeforeRouteLeave(to: Route, next: any) {
        if (this.saveClicked || this.continue || this.fetchingActive) {
            next();
        } else {
            this.nextRoute = to.name ? to.name : "";
            this.routeParams = to.params;
            this.retrieveData();
            const noChanges = this.lodash.isEqual(this.priorUserState, this.user);
            if (noChanges) {
                next();
            } else {
                next(false);
                (this.$refs.UnsavedFormModal as any).open();
            }
        }
    }

    isEqual(obj1: any, obj2: any) {
        var numOfEqualProps = 0;
        var numOfKeys = Object.keys(obj1).length;
        for (const [key1, value1] of Object.entries(obj1)) {
            for (const [key2, value2] of Object.entries(obj2)) {
                if (key1 === key2) {
                    if (JSON.stringify(value1) === JSON.stringify(value2)) {
                        numOfEqualProps++;
                    }
                }
            }
        }
        return numOfEqualProps === numOfKeys;
    }

    onContinue(data: boolean) {
        this.continue = data;
        if (data) this.$router.push({ name: this.nextRoute, params: this.routeParams });
        this.nextRoute = "";
    }

    async save(back: boolean) {
        this.saveClicked = true;
        await this.childComponentsHandler();

        if (!this.formState) {
            this.saveClicked = false;
            return;
        }
        if (!this.editMode) {
            createUser(this.user).then((id: string) => {
                if (back) this.$router.push("/users");
                else {
                    this.localId = id;
                    this.user.id = id;
                    this.usersLoaded = true;
                    this.aclLoaded = true;
                    this.priorUserState = JSON.parse(JSON.stringify(this.user)) as UserDetailsDto;
                    this.saveClicked = false;
                    this.fetchData();
                }
            }).catch(error => {
                (this.$refs.userErrorModal as any).open(error.response.data[0].description);
            });
            return;
        }
        updateUser(this.user).then(() => {
            this.priorUserState = JSON.parse(JSON.stringify(this.user)) as UserDetailsDto;
            this.saveClicked = false;
            if (back) this.$router.push("/users");
            else this.fetchData();
        });
    }

    fetchData() {
        getAvailableRoles(this.currentUser.id).then((response: any) => {
            this.availableRoles = response;
        });
        if (this.editMode) {
            this.fetchingActive = true;
            getUser(this.localId).then((response: any) => {
                response.password = "";
                response.repeatNewPassword = "";
                this.user = response;
                bus.$emit("documentTitle", this.user.firstName + " " + this.user.lastName);
                this.localUser = JSON.parse(JSON.stringify(response));
                this.localUser.password = "";
                this.localUser.sendPasswordChangeEmail = false;
                this.localUser.repeatPassword = "";
                this.localUser.id = this.localId;
                this.usersLoaded = true;

                getUserAcl(this.localId).then((response: Acl[]) => {
                    this.acl = response;
                    this.priorUserState.acl = JSON.parse(JSON.stringify(this.acl));
                    this.aclLoaded = true;
                });

                getUserInheritedAcl(this.localId).then((response: EffectivePermissionAcl[]) => {
                    if (response) {
                        this.inheritedAcls = JSON.parse(JSON.stringify(response));
                    }
                });
                this.fetchingActive = true;
            });
        }
    }

    async childComponentsHandler() {
        const validators = [] as any[];
        const components = this.$refs.childComponent as any[];
        components.forEach(component => {
            if (component.validators) { validators.push(component.validators); }
            component.emitData();
        });

        this.formState = await validateForm(validators);
    }

    retrieveData() {
        const components = this.$refs.childComponent as any[];
        components.forEach(component => {
            component.emitData();
        });
    }

    // drag & drop event handlers
    drag(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.target.id);
        if (!component) { return; }

        event.dataTransfer.setDragImage((this.$refs[component.componentName] as any)[0], 1000, 0);
        event.dataTransfer.setData(
            "id",
            event.target.id
        );

        component.active = true;
    }

    dragLeave(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        component.activeDropTarget = false;
    }

    dragOver(event: any) {
        event.preventDefault();
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        if (component.active) return;
        component.activeDropTarget = true;
    }

    dragEnd() {
        this.componentsData.forEach(x => {
            x.activeDropTarget = false;
            x.active = false;
        });
    }

    swapComponents(event: any) {
        event.preventDefault();
        if (!event.currentTarget) {
            return;
        }

        const target = this
            .componentsData
            .find(x => event.currentTarget && x.componentName === event.currentTarget.id);
        const source = this
            .componentsData
            .find(x => event.dataTransfer && x.componentName === event.dataTransfer.getData("id"));

        if (!(source && target)) { return; }

        const sourceOrder = source.order;
        source.order = target.order;
        target.order = sourceOrder;

        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => ({ componentName: x.componentName, sectionName: x.sectionName, order: x.order, collapseState: x.collapseState })));
    }

    // event callbacks
    onDetailsUpdated(data: any) {
        const details = JSON.parse(data);
        this.user.firstName = details.firstName || "";
        this.user.lastName = details.lastName || "";
        this.user.email = details.email || "";
        this.user.sendPasswordChangeEmail = details.sendPasswordChangeEmail || false;
        this.user.password = details.password || "";
        this.user.repeatPassword = details.repeatPassword || "";
        this.user.jobTitle = details.jobTitle;
        this.user.timeZone = details.timeZone || null;
        this.user.cultureCode = details.cultureCode;
        this.user.editorProfileId = details.editorProfileId;
        this.user.roles = details.roles;
    }

    onAclUpdated(acl: Acl[]) {
        if (!this.user.acl) {
            this.user.acl = [] as Acl[];
        }

        this.user.acl = this.user.acl
            .concat(acl
                .filter(acl => !this.user.acl.map(acl => acl.resourceId)
                    .includes(acl.resourceId)));
    }

    onUserGroupsUpdated(userGroups: string[]) {
        this.user.userGroupIds = userGroups;
    }

    onInitUserGroups(data: string[]) {
        this.priorUserState.userGroupIds = data;
        this.user.userGroupIds = data;
    }

    onSetDefaultValues(data: any) {
        this.priorUserState.editorProfileId = data;
    }

    onComponentStateChanged(componentState: any) {
        const component = this.componentsData.find(x => x.componentName === componentState.name);
        if (!component) return;

        component.collapseState = componentState.state;
        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => ({ componentName: x.componentName, order: x.order, collapseState: x.collapseState } as FormComponent)));
    }
}
